import { useEffect, useState } from 'react';
import { Warning } from 'shared/icons';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import Modal from '../../ui/Modal';
import { MONO_CLASS, Mono } from '../../ui/Mono';
import Textarea from '../../ui/Textarea';
import { useToast } from '../../ui/Toast';
import { ProductPicker } from '../lpnPickers';
import { CreateProductionOrder, GetKitInventoryIds } from './productionApi';
import { useProductionLabels } from './productionLabels';

/**
 * NOVA ORDEM DE PRODUÇÃO (console do Supervisor).
 *
 * A OP é **produzir o produto X a partir do produto Y, em kits de Z**. Ela não
 * tem máquina nem posição: a bancada a abre numa máquina, e a entrada e a saída
 * são as casadas com essa máquina (aba Máquinas).
 */
const ProductionOrderCreateModal = ({ isOpen, onClose, onCreated }) => {
    const { label } = useProductionLabels();
    const toast = useToast();

    const [productIn, setProductIn] = useState(null);
    const [product, setProduct] = useState(null);
    // Unidades por kit: obrigatório, padrão 1.
    const [kitSize, setKitSize] = useState('1');
    const [notes, setNotes] = useState('');
    // inventory_id do kit: AUTOMÁTICO, vem do SKU de saída (anúncios do Full).
    // null = nenhum produto escolhido; { loading } enquanto consulta.
    const [kitInventory, setKitInventory] = useState(null);
    const [plannedKits, setPlannedKits] = useState('');
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState(null);


    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        // Estado zerado a cada abertura: um formulário que "lembra" a OP
        // anterior faz o Supervisor criar a mesma ordem duas vezes.
        setProductIn(null);
        setProduct(null);
        setPlannedKits('');
        setKitSize('1');
        setNotes('');
        setKitInventory(null);
        setFormError(null);
        return undefined;
    }, [isOpen]);

    // O produto de saída define o inventory_id que a bancada vai conferir.
    const productOutId = product?.product_id ?? null;
    useEffect(() => {
        if (!productOutId) {
            setKitInventory(null);
            return undefined;
        }
        let alive = true;
        setKitInventory({ loading: true, ids: [] });
        GetKitInventoryIds(productOutId)
            .then((data) => {
                if (alive) {
                    setKitInventory({ loading: false, ids: Array.isArray(data?.inventory_ids) ? data.inventory_ids : [] });
                }
            })
            .catch((error) => {
                if (alive) {
                    setKitInventory({ loading: false, ids: [], error: error?.message || 'erro' });
                }
            });
        return () => {
            alive = false;
        };
    }, [productOutId]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        // O SKU de ENTRADA e o de SAÍDA: o pallet da entrada de fábrica só pode
        // conter o produto de entrada.
        if (!productIn?.product_id) {
            setFormError({ field: 'product_in', message: label('wms.common.required') });
            return;
        }
        if (!product?.product_id) {
            setFormError({ field: 'product', message: label('wms.common.required') });
            return;
        }
        const kitSizeNumber = Number(String(kitSize).trim());
        if (String(kitSize).trim() === '' || !Number.isInteger(kitSizeNumber) || kitSizeNumber < 1) {
            setFormError({ field: 'kit_size', message: label('wms.production.kit_size_required') });
            return;
        }
        // Sem inventory_id no SKU de saída a bancada não tem o que conferir.
        if (!kitInventory || kitInventory.loading || kitInventory.ids.length === 0) {
            setFormError({ field: 'product', message: label('wms.production.inventory_id_missing') });
            return;
        }
        const payload = {
            product_in_id: productIn.product_id,
            product_out_id: product.product_id,
            kit_size: kitSizeNumber,
            // Sem máquina: o gestor só cria; a bancada abre a OP liberada.
            // Meta opcional: o kit que a alcança finaliza a OP.
            planned_kits: String(plannedKits).trim() === '' ? null : Number(plannedKits),
            notes: notes.trim() === '' ? null : notes.trim(),
        };

        setSaving(true);
        setFormError(null);
        try {
            const created = await CreateProductionOrder(payload);
            toast.success(`${label('wms.entity.production_order')} ${created?.code ?? ''}`.trim());
            onCreated?.(created);
            onClose?.();
        } catch (error) {
            setFormError({ message: error?.message || 'Erro ao salvar' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={saving ? undefined : onClose} title={label('wms.production.new_order')} size="2xl">
            <form onSubmit={handleSubmit} className="space-y-5">
                {/* Sem máquina: a OP é escolhida na bancada (como as OS). */}
                <ProductPicker
                    label={label}
                    fieldLabel={label('wms.field.product_in')}
                    value={productIn}
                    onChange={(row) => {
                        setProductIn(row);
                        setFormError(null);
                    }}
                    disabled={saving}
                />
                {formError?.field === 'product_in' ? (
                    <p className="text-sm text-danger-600 dark:text-danger-400">{formError.message}</p>
                ) : null}
                <p className="-mt-3 text-xs text-mono-500 dark:text-mono-400">
                    {label('wms.production.product_in_help')}
                </p>

                <ProductPicker
                    label={label}
                    fieldLabel={label('wms.field.product_out')}
                    value={product}
                    onChange={(row) => {
                        setProduct(row);
                        setFormError(null);
                    }}
                    disabled={saving}
                />

                {formError?.field === 'product' ? (
                    <p className="text-sm text-danger-600 dark:text-danger-400">{formError.message}</p>
                ) : null}

                {/* O inventory_id do kit: automático, do SKU de saída — só leitura. */}
                {kitInventory ? (
                    <div className="-mt-2 rounded-lg border border-mono-200 px-3 py-2 dark:border-mono-800">
                        <p className="text-xs uppercase tracking-wide text-mono-500">
                            {label('wms.production.inventory_id')}
                        </p>
                        {kitInventory.loading ? (
                            <p className="text-sm text-mono-500">…</p>
                        ) : kitInventory.ids.length > 0 ? (
                            <>
                                <Mono className="block text-sm font-medium text-black dark:text-white">
                                    {kitInventory.ids.join(' · ')}
                                </Mono>
                                <p className="mt-1 text-xs text-mono-500 dark:text-mono-400">
                                    {label('wms.production.inventory_id_help')}
                                </p>
                            </>
                        ) : (
                            <p className="flex items-center gap-1.5 text-sm font-semibold text-danger-600 dark:text-danger-400">
                                <Warning size={16} className="shrink-0" />
                                {kitInventory.error || label('wms.production.inventory_id_missing')}
                            </p>
                        )}
                    </div>
                ) : null}

                <div>
                    <Input
                        label={label('wms.field.kit_size')}
                        type="number"
                        min={1}
                        value={kitSize}
                        onChange={(event) => {
                            setKitSize(event.target.value);
                            setFormError(null);
                        }}
                        disabled={saving}
                        className={MONO_CLASS}
                        autoComplete="off"
                    />
                    {formError?.field === 'kit_size' ? (
                        <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{formError.message}</p>
                    ) : null}
                    <p className="mt-1 text-xs text-mono-500 dark:text-mono-400">
                        {label('wms.production.kit_size_help')}
                    </p>
                </div>

                <div>
                    <Input
                        label={label('wms.production.planned_kits')}
                        type="number"
                        min={1}
                        value={plannedKits}
                        onChange={(event) => setPlannedKits(event.target.value)}
                        disabled={saving}
                        className={MONO_CLASS}
                        autoComplete="off"
                    />
                    <p className="mt-1 text-xs text-mono-500 dark:text-mono-400">
                        {label('wms.production.planned_kits_help')}
                    </p>
                </div>

                <Textarea
                    label={label('wms.field.notes')}
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    rows={2}
                    disabled={saving}
                    maxLength={2000}
                />

                {formError && !formError.field ? (
                    <p role="alert" className="text-sm text-danger-600 dark:text-danger-400">
                        {formError.message}
                    </p>
                ) : null}

                <div className="flex flex-col gap-3 pt-2 sm:flex-row">
                    <Button type="submit" loading={saving} className="flex-1">
                        {label('wms.common.save')}
                    </Button>
                    <Button type="button" variant="secondary" onClick={onClose} disabled={saving} className="sm:w-auto">
                        {label('wms.common.cancel')}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default ProductionOrderCreateModal;
